import { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { BookOpen, CalendarClock, Users } from "lucide-react";
import { api } from "@/lib/api";

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-soma-border bg-soma-surface2 p-6">
      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-soma-brand/15 text-soma-brand">
        <Icon size={20} />
      </div>
      <p className="text-3xl font-semibold text-soma-on" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
        {value}
      </p>
      <p className="mt-1 text-sm text-soma-muted">{label}</p>
    </div>
  );
}

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/overview")
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-6 md:p-10">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-32 animate-pulse rounded-2xl bg-soma-surface2" />
          ))}
        </div>
      </div>
    );
  }

  const trend = (data?.trend || []).map((t) => ({ ...t, day: t.date.slice(5) }));

  return (
    <div className="soma-grain min-h-screen p-6 md:p-10">
      <header className="mb-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-soma-brand">// keeper dashboard</p>
        <h1 className="mt-1 font-display text-4xl font-medium text-soma-on">Ecosystem Overview</h1>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Kpi icon={BookOpen} label="Total Readings" value={data.total_readings} />
        <Kpi icon={CalendarClock} label="Readings This Month" value={data.readings_this_month ?? 0} />
        <Kpi
          icon={Users}
          label="Active Practitioners"
          value={`${data.active_practitioners} / ${data.total_practitioners}`}
        />
      </div>

      <div className="mt-8 rounded-2xl border border-soma-border bg-soma-surface2 p-6">
        <h2 className="mb-6 font-display text-2xl text-soma-on">Readings Trend</h2>
        {trend.length === 0 ? (
          <p className="py-16 text-center text-soma-muted">No readings yet. The first seeker is on their way.</p>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2A3330" vertical={false} />
                <XAxis dataKey="day" stroke="#8A938F" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#8A938F" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: "rgba(212,184,114,0.08)" }}
                  contentStyle={{
                    background: "#121A18",
                    border: "1px solid #2A3330",
                    borderRadius: 12,
                    color: "#F4EFE6",
                  }}
                />
                <Bar dataKey="count" fill="#D4B872" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
